// ═══ PUBLISH ══════════════════════════════════════════════
// New feature (not part of the ported legacy engine): lets the builder turn a
// form into a publicly-fillable link. The Publish button is only enabled once
// the form has at least one question with a real label (see validation.ts) —
// the server re-checks the same rule on POST /api/forms/:id/publish rather
// than trusting this client-side gate.

import type { Ctx } from './engineTypes.ts';
import { hasValidQuestion } from './validation.ts';
import { publishForm, unpublishForm } from './api.ts';

export function attachPublish(ctx: Ctx): void {
  const doc = ctx.container;
  const state = ctx.state;
  const toast = ctx.toast;
  const $ = (id: string): any => doc.querySelector('#' + id);

  let status: 'draft' | 'published' = 'draft';
  let slug: string | null = null;

  function shareUrl(): string {
    return window.location.origin + '/f/' + (slug || ctx.formId);
  }

  // Two buttons drive the same publish state: the main builder toolbar
  // (#publish-btn) and the Preview tab's header (#publish-btn-rt).
  function buttons(): any[] {
    return [$('publish-btn'), $('publish-btn-rt')].filter(Boolean);
  }

  function render() {
    if (status === 'published') {
      buttons().forEach((btn) => {
        btn.textContent = '✓ Published';
        btn.disabled = false;
        btn.title = 'This form is live — click to view or manage the share link';
        btn.classList.remove('primary');
        btn.classList.add('excel');
      });
    } else {
      const ok = hasValidQuestion(state.questions);
      buttons().forEach((btn) => {
        btn.textContent = '🌐 Publish';
        btn.disabled = !ok;
        btn.title = ok
          ? 'Publish this form and get a shareable link anyone can fill in'
          : 'Add at least one question with a label to publish';
        btn.classList.remove('excel');
        btn.classList.add('primary');
      });
    }
  }

  function openModal() {
    $('publish-link-inp').value = shareUrl();
    $('publish-modal-title').textContent = status === 'published' ? 'Form published' : 'Publish this form';
    $('publish-unpublish-btn').classList.toggle('hide', status !== 'published');
    $('publish-modal-overlay').classList.remove('hide');
  }
  function closeModal() { $('publish-modal-overlay').classList.add('hide'); }

  async function onPublishClick() {
    if (status === 'published') { openModal(); return; }
    if (!hasValidQuestion(state.questions)) {
      toast('Add at least one question with a label before publishing.', 'err');
      return;
    }
    try {
      const res = await publishForm(ctx.formId);
      status = 'published';
      slug = res?.publicSlug || slug;
      render();
      openModal();
      toast('Form published!', 'ok');
    } catch (e: any) {
      toast(e?.response?.data?.error || 'Could not publish form.', 'err', 5000);
    }
  }

  async function onCopyClick() {
    const url = shareUrl();
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied to clipboard.', 'ok', 2000);
    } catch {
      const inp = $('publish-link-inp'); inp.focus(); inp.select();
      toast('Copy failed — select the link and press Ctrl+C.', 'info', 3000);
    }
  }

  async function onUnpublishClick() {
    try {
      await unpublishForm(ctx.formId);
      status = 'draft';
      render();
      closeModal();
      toast('Form unpublished — the link no longer works.', 'info');
    } catch {
      toast('Could not unpublish form.', 'err');
    }
  }

  buttons().forEach((btn) => { btn.onclick = onPublishClick; });
  $('publish-link-copy').onclick = onCopyClick;
  $('publish-unpublish-btn').onclick = onUnpublishClick;
  $('publish-modal-close').onclick = closeModal;
  $('publish-modal-done').onclick = closeModal;
  $('publish-modal-overlay').addEventListener('click', (e: any) => {
    if (e.target?.id === 'publish-modal-overlay') closeModal();
  });

  ctx.updatePublishState = render;
  ctx.setPublishStatus = (s: string, publicSlug?: string | null) => {
    status = s === 'published' ? 'published' : 'draft';
    slug = publicSlug || null;
    render();
  };
}
